import React, { Key, Ref, ForwardedRef, forwardRef, useRef } from 'react';
import clsx from 'clsx';
import { PressResponder, useHover } from '@react-aria/interactions';
import { useSelect } from '@react-aria/select';
import { mergeProps } from '@react-aria/utils';
import { useFocusableRef } from '@react-spectrum/utils';
import { useSelectState } from '@react-stately/select';
import { ChevronDown } from '@danske/sapphire-icons/react';

import { HiddenSelect } from './HiddenSelect';
import { Button } from '../../Button';
import {
  ListBoxPopover,
  SapphireStyleProps,
  useButton,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { SelectProps } from './SelectProps';
import { Node } from '@react-types/shared';

/**
 * The SelectButtons shares a lot with the Select, but not everything.
 * So we must pick only the relevant props.
 */
export interface SelectButtonProps<
  T,
  M extends 'single' | 'multiple' = 'single'
> extends Omit<SelectProps<T, M>, 'size'>,
    SapphireStyleProps {
  /**
   * Visual variations
   * @default 'tertiary'
   */
  variant?: 'primary' | 'secondary' | 'tertiary';

  /**
   * @default 'md'
   */
  size?: 'lg' | 'md' | 'sm';

  /**
   * Handler that is called when the selection changes.
   *
   * @deprecated Use `onChange` instead.
   */
  onSelectionChange?: (key: Key | null) => void;

  /**
   * Function to customize the rendered text in the field for a given selection.
   * Note that if there is only one item seleted, that one is rendered in the field.
   * @default (selectedItems) => `${selectedItems.length} selected`
   */
  renderSelectionText?: (selectedItems: Node<T>[]) => string;
}

/**
 * This component is a simple version of select having secondary text button as
 * a trigger element which can be used along with components like Table.
 * Allows user to select from a list of options, similar to native select
 * component, but with an improved UI.
 *
 */
function _SelectButton<T extends object>(
  props: SelectButtonProps<T>,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const {
    placeholder,
    children,
    isDisabled,
    variant = 'tertiary',
    size = 'md',
    ...otherProps
  } = props;
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useFocusableRef<HTMLButtonElement>(null);
  const listBoxRef = useRef<HTMLUListElement>(null);
  const state = useSelectState(props);
  const { triggerProps, valueProps, menuProps } = useSelect(
    { ...props },
    state,
    triggerRef
  );
  const { styleProps } = useSapphireStyleProps(otherProps);
  const { hoverProps } = useHover({ isDisabled });
  const { buttonProps } = useButton(
    {
      ...triggerProps,
      elementType: 'button',
    },
    triggerRef
  );

  const {
    renderSelectionText = (selection) => `${selection.length} selected`,
  } = props;

  const buttonLabel =
    state.selectedItems.length === 0
      ? placeholder
      : state.selectedItems.length === 1
      ? state.selectedItems[0].rendered
      : renderSelectionText(state.selectedItems);

  return (
    <div
      ref={ref}
      className={clsx(styleProps.className)}
      style={{
        ...styleProps.style,
        position: 'relative' /* important. See description of HiddenSelect */,
      }}
    >
      <HiddenSelect
        {...props}
        label={props['aria-label'] || props.placeholder}
        state={state}
        triggerRef={triggerRef}
      />
      <PressResponder
        ref={triggerRef}
        isPressed={state.isOpen}
        {...mergeProps(buttonProps, hoverProps, valueProps)}
      >
        <Button
          variant={variant}
          size={size}
          icon={<ChevronDown />}
          iconAlign="right"
          isDisabled={isDisabled}
        >
          {buttonLabel}
        </Button>
      </PressResponder>
      <ListBoxPopover<T>
        state={state}
        triggerRef={triggerRef}
        listBoxRef={listBoxRef}
        listBoxProps={menuProps}
        popoverRef={popoverRef}
        size={size}
        selectWidth={triggerRef.current?.offsetWidth}
      >
        {children}
      </ListBoxPopover>
    </div>
  );
}

export const SelectButton = forwardRef(_SelectButton as any) as <
  T extends object,
  M extends 'single' | 'multiple' = 'single'
>(
  props: SelectButtonProps<T, M> & { ref?: Ref<HTMLDivElement> }
) => React.ReactElement;
